/**
 * Expenses API + TanStack Query hooks (Prompt 11E.8 §6).
 *
 * Mirrors the pattern in @features/attendance/api/leaves.ts — hand-declared
 * types matching backend/app/features/expenses/schemas.py exactly (this is
 * a brand-new backend module, not yet in the generated shared/api-types).
 *
 * Endpoints:
 *   POST /v1/expenses                                  — create
 *   GET  /v1/expenses                                   — list (year/month/category/status filter)
 *   GET  /v1/expenses/{id}
 *   POST /v1/expenses/{id}/void                         — reason required, no hard delete
 *   POST /v1/expenses/{id}/attachments                  — multipart upload
 *   GET  /v1/expenses/{id}/attachments/{filename}/url    — short-TTL signed URL
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@core/api/client';

export type ExpenseCategory =
  | 'rent'
  | 'utilities'
  | 'supplies'
  | 'maintenance'
  | 'salary_advance'
  | 'other';
export type ExpenseStatus = 'active' | 'void';

export const EXPENSE_CATEGORY_OPTIONS: { value: ExpenseCategory; label: string }[] = [
  { value: 'rent', label: 'Rent' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'salary_advance', label: 'Salary Advance' },
  { value: 'other', label: 'Other' },
];

export interface ExpenseAttachmentRef {
  storage_path: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  uploaded_by_uid: string;
}

export interface ExpenseResponse {
  id: string;
  date: string;
  amount: number; // paise
  category: ExpenseCategory;
  vendor: string | null;
  note: string | null;
  attachments: ExpenseAttachmentRef[];
  status: ExpenseStatus;
  created_by_uid: string;
  created_at: string;
  updated_at: string;
  voided_at: string | null;
  voided_by_uid: string | null;
  void_reason: string | null;
}

export interface ExpenseListResponse {
  items: ExpenseResponse[];
  total: number;
  total_amount: number; // paise
  has_more: boolean;
}

export interface ExpenseCreate {
  date: string;
  amount: number; // paise
  category: ExpenseCategory;
  vendor?: string;
  note?: string;
}

export interface ExpenseVoidRequest {
  reason: string;
}

export interface AttachmentUrlResponse {
  download_url: string;
  expires_at: string;
}

export interface ExpensesFilters {
  year?: number;
  month?: number;
  category?: ExpenseCategory;
  status?: ExpenseStatus;
}

const expensesKeys = {
  all: ['expenses'] as const,
  list: (filters: ExpensesFilters) => ['expenses', 'list', filters] as const,
  detail: (id: string) => ['expenses', 'detail', id] as const,
};

export function useExpenses(filters: ExpensesFilters = {}) {
  return useQuery({
    queryKey: expensesKeys.list(filters),
    queryFn: () =>
      api.get<ExpenseListResponse>('/v1/expenses', {
        query: {
          year: filters.year,
          month: filters.month,
          category: filters.category,
          status: filters.status,
        },
      }),
    staleTime: 30_000,
  });
}

export function useExpense(id: string | undefined) {
  return useQuery({
    queryKey: expensesKeys.detail(id ?? '__none__'),
    queryFn: () => api.get<ExpenseResponse>(`/v1/expenses/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExpenseCreate) => api.post<ExpenseResponse>('/v1/expenses', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expensesKeys.all });
    },
  });
}

export function useVoidExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<ExpenseResponse>(`/v1/expenses/${id}/void`, { reason } satisfies ExpenseVoidRequest),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expensesKeys.all });
    },
  });
}

export async function uploadExpenseAttachment(id: string, file: File): Promise<ExpenseResponse> {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<ExpenseResponse>(`/v1/expenses/${id}/attachments`, fd, { multipart: true });
}

export async function getExpenseAttachmentUrl(
  id: string,
  filename: string,
): Promise<AttachmentUrlResponse> {
  return api.get<AttachmentUrlResponse>(
    `/v1/expenses/${id}/attachments/${encodeURIComponent(filename)}/url`,
  );
}
